import React, { useContext, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useId } from '@fluentui/react-hooks';
import { Label, IOnRenderComboBoxLabelProps, ComboBox, Checkbox, MessageBar, MessageBarType, Link, MessageBarButton } from '@fluentui/react';

import { ApiError } from 'services/restService';
import { stepStyles as styles } from './commonStyles';
import { CustomLabel } from 'components/form/CustomLabel';
import { StepContainer } from 'components/common/steps/StepContainer';
import { StepBaseProps, ConfigurationInfo } from 'components/common/Wizard';
import { useOrgsAndSubsOptions, useResourceGroupOptions } from 'hooks/api';
import TranslationsContext from 'translations/TranslationsContext';
import translationKeys from 'translationKeys';

import { Trans } from 'react-i18next';

export interface ConfigurationProps extends StepBaseProps {
	isTerms?: boolean;
	children?: React.ReactChild[];
}

enum FormFieldName {
	OrgId = 'organizationId',
	SubId = 'subscriptionId',
	ResGroupId = 'resourceGroupId',
}

export const Configuration = ({ checkCanGoNext, stepRef, error: processError, resetErrors, setStepError, isTerms, children }: ConfigurationProps) => {
	const { t } = useContext(TranslationsContext)!;
	const fieldRefs = new Map<FormFieldName, any>([
		[FormFieldName.OrgId, useRef(null)],
		[FormFieldName.SubId, useRef(null)],
		[FormFieldName.ResGroupId, useRef(null)],
	]);

	const [termsConfirmed, setTerms] = React.useState(false);
	const [resourceGroups, setResourceGroupsId] = useResourceGroupOptions();
	const [orgsAndSubs, setOrgsAndSubs] = useOrgsAndSubsOptions();
	const { isLoading, error, orgOptions, subOptions } = orgsAndSubs;
	const [isValidationTriggered, setIsValidationTriggered] = React.useState(false);

	const inputId = useId('ComboBox');

	const { control, trigger, watch, getValues, formState } = useForm<ConfigurationInfo>({
		defaultValues: {
			organizationId: undefined,
			subscriptionId: undefined,
			resourceGroupId: undefined,
		}
	});

	watch((state) => {
		setResourceGroupsId(state.subscriptionId);
	});

	useEffect(() => {
		const errorFieldsNames = Object.keys(formState.errors);

		if (isValidationTriggered && errorFieldsNames.length) {
			const fieldRef = fieldRefs.get(errorFieldsNames[0] as FormFieldName);

			if (fieldRef) fieldRef.current.focus();

			setIsValidationTriggered(false);
		}
	}, [formState]);

	useEffect(() => {
		if (orgOptions.length && subOptions.length && isTerms) {
			checkCanGoNext(termsConfirmed);
		}
	}, [termsConfirmed]);

	useEffect(() => {
		if (error || processError) setStepError();
	}, [error, processError]);

	stepRef.current.validateStep = async () => {
		const res = await trigger();
		setIsValidationTriggered(true);

		return res;
	};

	stepRef.current.getDatafromStep = () => Promise.resolve(getValues());

	const resetAllErrors = () => {
		resetErrors();
		setOrgsAndSubs({
			...orgsAndSubs,
			error: null
		});
	};

	const renderForm = () => (
		<>
			{renderError(error || processError)}
			<StepContainer>
				<form noValidate aria-labelledby='form-title' className={styles.root}>
					{children}
					<Controller
						name={FormFieldName.OrgId}
						control={control}
						rules={{ required: true }}
						render={({ field, formState }) => (
							<ComboBox
								{...field}
								id={`${inputId}-org`}
								required
								allowFreeform
								label={t(translationKeys.Steps.Configuration.Organization.ComboBox.Label)}
								componentRef={fieldRefs.get(FormFieldName.OrgId)}
								dropdownWidth={300}
								autoComplete='off'
								disabled={!orgOptions.length}
								options={orgOptions}
								placeholder={t(translationKeys.Steps.Configuration.Organization.ComboBox.Placeholder)}
								className={styles.formInputElement}
								onChange={(e, option) => {
									field.onChange(option?.key as any);
									trigger(field.name);
								}}
								defaultSelectedKey={field.value}
								errorMessage={formState.errors[field.name] && t(translationKeys.Steps.Configuration.Organization.ComboBox.ErrorMessage)}
								onRenderLabel={({ props }: IOnRenderComboBoxLabelProps) =>
									<CustomLabel
										{...props}
										description={t(translationKeys.Steps.Configuration.Organization.ComboBox.Description)}
										iconTitle={t(translationKeys.Steps.Configuration.Organization.ComboBox.IconTitle)}
										iconAriaLabel={t(translationKeys.Steps.Configuration.Organization.ComboBox.IconAriaLabel)}
									/>}
							/>)
						}
					/>

					<Controller
						name={FormFieldName.SubId}
						control={control}
						rules={{ required: true }}
						render={({ field, formState }) => (
							<ComboBox
								{...field}
								required
								allowFreeform
								id={`${inputId}-sub`}
								label={t(translationKeys.Steps.Configuration.Subscription.ComboBox.Label)}
								componentRef={fieldRefs.get(FormFieldName.SubId)}
								dropdownWidth={300}
								autoComplete='off'
								disabled={!subOptions.length}
								options={subOptions}
								className={styles.formInputElement}
								placeholder={t(translationKeys.Steps.Configuration.Subscription.ComboBox.Placeholder)}
								onChange={(e, option) => {
									field.onChange(option?.key as any);
									trigger(field.name);
								}}
								defaultSelectedKey={field.value}
								errorMessage={formState.errors[field.name] && t(translationKeys.Steps.Configuration.Subscription.ComboBox.ErrorMessage)}
								onRenderLabel={({ props }: IOnRenderComboBoxLabelProps) =>
									<CustomLabel
										{...props}
										description={t(translationKeys.Steps.Configuration.Subscription.ComboBox.Description)}
										iconTitle={t(translationKeys.Steps.Configuration.Subscription.ComboBox.IconTitle)}
										iconAriaLabel={t(translationKeys.Steps.Configuration.Subscription.ComboBox.IconAriaLabel)}
									/>}
							/>)
						}
					/>
					<Controller
						name={FormFieldName.ResGroupId}
						control={control}
						rules={{ required: true }}
						render={({ field, formState }) => (
							<ComboBox
								{...field}
								id={`${inputId}-rg`}
								required
								allowFreeform
								label={t(translationKeys.Steps.Configuration.ResourceGroup.ComboBox.Label)}
								componentRef={fieldRefs.get(FormFieldName.ResGroupId)}
								dropdownWidth={300}
								autoComplete='off'
								options={resourceGroups.options}
								className={styles.formInputElement}
								placeholder={t(translationKeys.Steps.Configuration.ResourceGroup.ComboBox.Placeholder)}
								disabled={!resourceGroups.options.length}
								onChange={(e, option) => {
									field.onChange(option?.key as any);
									trigger(field.name);
								}}
								defaultSelectedKey={field.value}
								errorMessage={formState.errors[field.name] && t(translationKeys.Steps.Configuration.ResourceGroup.ComboBox.ErrorMessage)}
								onRenderLabel={({ props }: IOnRenderComboBoxLabelProps) =>
									<CustomLabel
										{...props}
										description={t(translationKeys.Steps.Configuration.ResourceGroup.ComboBox.Description)}
										iconTitle={t(translationKeys.Steps.Configuration.ResourceGroup.ComboBox.IconTitle)}
										iconAriaLabel={t(translationKeys.Steps.Configuration.ResourceGroup.ComboBox.IconAriaLabel)}
									/>}
							/>)
						}
					/>
					{isTerms && renderTerms()}
				</form>
			</StepContainer>
		</>
	);

	const renderTerms = () => (
		<>
			<div className={styles.line} />
			<Label className={styles.consentLable} required>{t(translationKeys.Steps.Configuration.Terms.Title)}</Label>
			<Checkbox id='termsId' required inputProps={{ 'aria-invalid': 'false' }} onChange={(e, checked) => setTerms(checked)} onRenderLabel={
				() => <label htmlFor='termsId'>{
					<Trans
						i18nKey={translationKeys.Steps.Configuration.Terms.Acknowledgement}
						components={{
							terms: <Link underline={true} target='_blank' href='https://aka.ms/mc4n/installer/tos' />,
							privacy: <Link underline={true} target='_blank' href='https://go.microsoft.com/fwlink/?LinkId=521839' />
						}}
					/>}
				</label>} />
		</>
	);

	const renderError = (error: ApiError) => {
		return error && <MessageBar
			messageBarType={MessageBarType.error}
			onDismiss={resetAllErrors}
			actions={<MessageBarButton target='_blank' href='https://admin.powerplatform.microsoft.com/support/'>{t(translationKeys.Steps.Configuration.Error.MessageBar.Actions.Button.Content)}</MessageBarButton>}
			isMultiline={false}
			dismissButtonAriaLabel={t(translationKeys.Steps.Configuration.Error.MessageBar.DismissAriaLabel)}
		>
			{t(translationKeys.Steps.Configuration.Error.MessageBar.Content.LeadingText)}
			{error.traceId && <span> {t(translationKeys.Steps.Configuration.Error.MessageBar.Content.ErrorId)}: {error.traceId}</span>}
		</MessageBar>;
	};

	return (isLoading ? null : renderForm());
};

Configuration.defaultProps = {
	isTerms: false
};
